/**
 * The proposal configurator.
 *
 * A four-step wizard over one autosaving draft. It is not a document editor:
 * approved content is never shown as editable text, and the only things Sales
 * supplies are the values the approved template left blank.
 *
 * Autosave runs continuously, so moving between steps needs no "save" and
 * leaving the page loses nothing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ApiError,
  STATUS_TRANSITIONS,
  endpoints,
  type PaymentTerm,
  type Proposal,
  type ProposalConfiguration,
  type CommercialSummary,
  type FamilyRules,
  type ReviewReport,
  type ProposalStatus,
  type QualityReport,
  type AIStatus,
  type FinalizationReport,
  type ProposalVersionSummary,
  type TemplateManifest,
} from '../lib/api';
import { useApiErrorHandler } from '../lib/auth';
import { useAutosave, type SaveStatus } from '../hooks/useAutosave';
import { Alert, Button, Card, Skeleton, StatusBadge, formatMoney } from '../components/ui';
import { nextStep, previousStep, stepsFor, type StepId, type WizardStep } from './wizard/steps';
import { ClientStep } from './wizard/ClientStep';
import { EngagementStep } from './wizard/EngagementStep';
import { ScopeStep } from './wizard/ScopeStep';
import { TimelineStep } from './wizard/TimelineStep';
import { CommercialsStep } from './wizard/CommercialsStep';
import { ReviewStep } from './wizard/ReviewStep';

const LOCKED: ProposalStatus[] = ['SUBMITTED', 'WON', 'LOST'];

export function ProposalPage() {
  const { proposalId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const handleError = useApiErrorHandler();

  const step = (searchParams.get('step') as StepId) ?? 'client';
  const setStep = useCallback(
    (next: StepId) => {
      setSearchParams({ step: next }, { replace: false });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setSearchParams],
  );

  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [manifest, setManifest] = useState<TemplateManifest | null>(null);
  const [rules, setRules] = useState<FamilyRules | null>(null);
  const [commercials, setCommercials] = useState<CommercialSummary | null>(null);
  const [review, setReview] = useState<ReviewReport | null>(null);
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>([]);
  const [quality, setQuality] = useState<QualityReport | null>(null);
  const [finalization, setFinalization] = useState<FinalizationReport | null>(null);
  const [versions, setVersions] = useState<ProposalVersionSummary[]>([]);
  // Fetched once. Null until it lands, and null renders no AI affordances,
  // which is also what a disabled provider renders.
  const [aiStatus, setAiStatus] = useState<AIStatus | null>(null);
  const [draft, setDraft] = useState<ProposalConfiguration>({});
  const [revision, setRevision] = useState(0);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const result = await endpoints.proposal(proposalId);
      setProposal(result.proposal);
      setDraft(result.proposal.configuration);
      setRevision(result.proposal.revision);

      const [manifestResult, terms, reviewResult] = await Promise.all([
        endpoints.templateManifest(result.proposal.templateId),
        endpoints.paymentTerms(),
        endpoints.review(proposalId),
      ]);
      setManifest(manifestResult.manifest);
      setRules(manifestResult.rules);
      setPaymentTerms(terms.paymentTerms);
      setReview(reviewResult);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        setError('This proposal does not exist, or you do not have access to it.');
        return;
      }
      setError(handleError(caught));
    }
  }, [proposalId, handleError]);

  useEffect(() => {
    void load();
  }, [load]);

  // Whether AI is available at all. A failure here is ignored on purpose: AI is
  // optional, so not knowing means not offering it, which is the safe default.
  useEffect(() => {
    endpoints
      .aiStatus()
      .then(setAiStatus)
      .catch(() => setAiStatus(null));
  }, []);

  const autosave = useAutosave<ProposalConfiguration>({
    revision,
    debounceMs: 800,
    onSave: async (value, currentRevision) => {
      const result = await endpoints.saveProposal(proposalId, value, currentRevision);
      setProposal(result.proposal);
      setRevision(result.proposal.revision);
      return { revision: result.proposal.revision };
    },
  });

  const update = useCallback(
    <K extends keyof ProposalConfiguration>(key: K, value: ProposalConfiguration[K]) => {
      setDraft((current) => {
        const next = { ...current, [key]: value };
        autosave.save(next);
        return next;
      });
    },
    [autosave],
  );

  // The Review step needs the server's verdict, so flush any pending edit first.
  const refreshQuality = useCallback(async () => {
    try {
      await autosave.flush();
      const [report, summary, reviewResult, finalReport, versionList] = await Promise.all([
        endpoints.quality(proposalId),
        endpoints.commercials(proposalId),
        endpoints.review(proposalId),
        endpoints.readiness(proposalId),
        endpoints.versions(proposalId),
      ]);
      setQuality(report);
      setCommercials(summary);
      setReview(reviewResult);
      setFinalization(finalReport);
      setVersions(versionList.versions);
    } catch (caught) {
      setError(handleError(caught));
    }
  }, [autosave, proposalId, handleError]);

  // The commercials step shows the live one-time / recurring split, so refresh
  // there too rather than only on Review.
  useEffect(() => {
    if ((step === 'review' || step === 'commercials') && proposal) void refreshQuality();
    // Deliberately not depending on refreshQuality: it changes identity on
    // every autosave, which would re-run this on each keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, proposal?.proposalId, revision]);

  const isLocked = useMemo(
    () => Boolean(proposal && LOCKED.includes(proposal.status)),
    [proposal],
  );

  /** Configuration paths the server flagged, for field-level highlighting. */
  const flagged = useMemo(() => {
    const paths = new Set<string>();
    for (const finding of quality?.findings ?? []) {
      if (finding.severity !== 'BLOCKER') continue;
      for (const field of finding.fields ?? []) paths.add(field);
    }
    return paths;
  }, [quality]);

  const runAction = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 422) {
        const report = (caught.details as { quality?: QualityReport } | undefined)?.quality;
        if (report) {
          setQuality(report);
          setStep('review');
          setError('Some details still need attention before this can be generated.');
          return;
        }
        setError(caught.message);
        return;
      }
      setError(handleError(caught));
    } finally {
      setBusy(false);
    }
  };

  const generate = () =>
    runAction(async () => {
      await autosave.flush();
      const result = await endpoints.generateDocument(proposalId);
      setNotice(
        result.scan.ok
          ? `Document generated — version ${result.version}, ${Math.round(result.documentBytes / 1024)} KB.`
          : `Generated, but ${result.scan.summary.totalCritical} placeholder(s) are unresolved: ${result.scan.summary.criticalTokens.join(', ')}`,
      );
      await load();
      await refreshQuality();
      // Offer the file immediately: generate-then-download is one action to a
      // user, even though it is two requests.
      const link = await endpoints.downloadUrl(proposalId);
      window.location.assign(link.url);
    });

  const download = () =>
    runAction(async () => {
      const result = await endpoints.downloadUrl(proposalId);
      window.location.assign(result.url);
    });

  const acknowledgeLeakage = (findingIds: string[]) =>
    runAction(async () => {
      await endpoints.acknowledgeLeakage(proposalId, findingIds);
      await refreshQuality();
      setNotice(
        findingIds.length === 1
          ? 'Reference acknowledged.'
          : `${findingIds.length} references acknowledged.`,
      );
    });

  /**
   * Mark final.
   *
   * The server decides. A 422 comes back with the gate's own reasons, which are
   * shown verbatim rather than paraphrased — the salesperson needs to know
   * which check refused, not a friendlier version of it.
   */
  const markFinal = () =>
    runAction(async () => {
      try {
        await endpoints.changeStatus(proposalId, 'FINAL');
        setNotice('Proposal marked final.');
        await load();
        await refreshQuality();
      } catch (caught) {
        if (caught instanceof ApiError && caught.status === 422) {
          const gate = (caught.details as { gate?: FinalizationReport } | undefined)?.gate;
          if (gate) {
            setFinalization({ ...gate, proposalNumber: proposal?.proposalNumber ?? '', status: proposal?.status ?? 'DRAFT' });
            setStep('review');
            setError(`Cannot mark final yet: ${gate.reasons.join(' · ')}`);
            return;
          }
        }
        throw caught;
      }
    });

  if (error && !proposal) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <Alert tone="error">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate('/')}>
          Back to dashboard
        </Button>
      </div>
    );
  }

  if (!proposal || !manifest || !rules || !review) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const stepProps = {
    config: draft,
    update,
    disabled: isLocked,
    flagged,
    manifest,
    paymentTerms,
    proposalId,
    aiStatus,
  };

  // Steps are composed per proposal, so no screen appears that does not apply
  // to this service.
  const steps = stepsFor({
    hasEngagementFields: rules.sections.some((section) => section.fields.length > 0),
    isCustomMode: draft.mode === 'CUSTOM',
  });
  const activeStep: StepId = steps.some((s) => s.id === step) ? step : 'client';
  const previous = previousStep(steps, activeStep);
  const next = nextStep(steps, activeStep);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold text-ink">{proposal.proposalNumber}</h1>
            <StatusBadge status={proposal.status} />
            {draft.mode === 'CUSTOM' && (
              <span className="rounded-full bg-tint px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-teal">
                Customized
              </span>
            )}
            <SaveIndicator status={autosave.status} lastSavedAt={autosave.lastSavedAt} />
          </div>
          <p className="mt-1 text-[13px] text-muted">
            {proposal.templateName}
            {proposal.clientCompanyName ? ` · ${proposal.clientCompanyName}` : ''}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/')} disabled={busy}>
            Dashboard
          </Button>
          {proposal.latestDocumentVersion !== null && (
            <Button variant="secondary" onClick={download} disabled={busy}>
              Download v{proposal.latestDocumentVersion}
            </Button>
          )}
        </div>
      </header>

      {autosave.status === 'conflict' && (
        <Alert tone="warning" title="Changed elsewhere">
          This proposal was updated in another tab or by another user.{' '}
          <button
            type="button"
            onClick={() => {
              autosave.reset();
              void load();
            }}
            className="font-semibold underline"
          >
            Reload to continue
          </button>
          .
        </Alert>
      )}
      {error && <Alert tone="error">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}
      {isLocked && (
        <Alert tone="info">
          This proposal is {proposal.status.toLowerCase()} and is no longer editable.
        </Alert>
      )}

      <WizardNav steps={steps} current={activeStep} onGoToStep={setStep} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div>
          {activeStep === 'client' && <ClientStep {...stepProps} />}
          {activeStep === 'engagement' && (
            <EngagementStep
              config={draft}
              update={update}
              disabled={isLocked}
              flagged={flagged}
              rules={rules}
            />
          )}
          {activeStep === 'scope' && (
            <ScopeStep
              config={draft}
              update={update}
              disabled={isLocked}
              manifest={manifest}
              defaultExecutiveSummary={review.defaultExecutiveSummary}
              proposalId={proposalId}
              aiStatus={aiStatus}
            />
          )}
          {activeStep === 'timeline' && (
            <TimelineStep {...stepProps} allowActivityEditing={draft.mode === 'CUSTOM'} />
          )}
          {activeStep === 'commercials' && (
            <CommercialsStep {...stepProps} rules={rules} commercials={commercials} />
          )}
          {activeStep === 'review' && (
            <ReviewStep
              proposal={proposal}
              config={draft}
              manifest={manifest}
              rules={rules}
              commercials={commercials}
              quality={quality}
              review={review}
              finalization={finalization}
              versions={versions}
              acknowledging={busy}
              onAcknowledgeLeakage={acknowledgeLeakage}
              onGoToStep={setStep}
            />
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-rule pt-5">
            {previous && (
              <Button variant="secondary" onClick={() => setStep(previous)} disabled={busy}>
                Back
              </Button>
            )}
            {next ? (
              <Button onClick={() => setStep(next)}>
                Continue to {steps.find((s) => s.id === next)?.label}
              </Button>
            ) : (
              <Button
                onClick={generate}
                loading={busy}
                disabled={isLocked || quality?.status === 'NEEDS_ATTENTION'}
              >
                Generate DOCX
              </Button>
            )}
            {activeStep === 'review' &&
              proposal.latestDocumentVersion !== null &&
              STATUS_TRANSITIONS[proposal.status].includes('FINAL') && (
              <Button
                variant="secondary"
                onClick={markFinal}
                loading={busy}
                disabled={isLocked || finalization?.canFinalize === false}
                title={
                  finalization?.canFinalize === false
                    ? finalization.reasons.join(' · ')
                    : undefined
                }
              >
                Mark final
              </Button>
              )}
            {activeStep === 'review' && quality?.status === 'NEEDS_ATTENTION' && (
              <span className="text-[13px] text-muted">
                Resolve the items above to generate.
              </span>
            )}
          </div>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <Card title="Summary">
            <dl className="space-y-2.5 text-[13px]">
              <SummaryRow label="Client" value={draft.clientCompanyName || 'Not set'} />
              <SummaryRow label="Type" value={proposal.templateName} />
              <SummaryRow
                label="Investment"
                value={formatMoney(proposal.investmentTotal, proposal.currency)}
              />
              <SummaryRow
                label="Document"
                value={
                  proposal.latestDocumentVersion
                    ? `Version ${proposal.latestDocumentVersion}`
                    : 'Not generated'
                }
              />
            </dl>
          </Card>

          <div className="rounded-lg border border-rule bg-tint px-4 py-3 text-[12px] text-muted">
            <p className="font-medium text-ink">
              {draft.mode === 'CUSTOM' ? 'Customize Proposal' : 'Quick Proposal'}
            </p>
            <p className="mt-1">
              {draft.mode === 'CUSTOM'
                ? 'Approved content is included by default. Anything you edit or add is tracked and flagged for review.'
                : 'Approved scope, deliverables, differentiators, assumptions and next steps are included automatically. You supply only the client, timing and commercials.'}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function WizardNav({
  steps,
  current,
  onGoToStep,
}: {
  steps: WizardStep[];
  current: StepId;
  onGoToStep(step: StepId): void;
}) {
  const currentIndex = steps.findIndex((s) => s.id === current);
  return (
    <nav aria-label="Proposal steps">
      <ol
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}
      >
        {steps.map((entry, index) => {
          const active = entry.id === current;
          const done = index < currentIndex;
          return (
            <li key={entry.id}>
              <button
                type="button"
                onClick={() => onGoToStep(entry.id)}
                aria-current={active ? 'step' : undefined}
                className={[
                  'w-full rounded-lg border px-3 py-2.5 text-left transition-colors',
                  active
                    ? 'border-teal bg-tint'
                    : 'border-rule hover:border-muted hover:bg-tint',
                ].join(' ')}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={[
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
                      active
                        ? 'bg-teal text-white'
                        : done
                          ? 'bg-white text-teal ring-1 ring-teal'
                          : 'bg-white text-muted ring-1 ring-rule',
                    ].join(' ')}
                  >
                    {done ? '✓' : index + 1}
                  </span>
                  <span
                    className={[
                      'text-[13px] font-medium',
                      active ? 'text-ink' : 'text-body',
                    ].join(' ')}
                  >
                    {entry.label}
                  </span>
                </span>
                <span className="mt-1 block pl-7 text-[11px] text-muted">{entry.hint}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="truncate text-right font-medium text-ink">{value}</dd>
    </div>
  );
}

/** "Saving…" / "Saved", the feedback that makes autosave trustworthy. */
function SaveIndicator({
  status,
  lastSavedAt,
}: {
  status: SaveStatus;
  lastSavedAt: Date | null;
}) {
  const text: Record<SaveStatus, string> = {
    idle: lastSavedAt ? 'Saved' : '',
    pending: 'Saving…',
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Not saved',
    conflict: 'Changed elsewhere',
  };
  const label = text[status];
  if (!label) return null;

  const tone =
    status === 'error' || status === 'conflict'
      ? 'text-red-700'
      : status === 'saved' || status === 'idle'
        ? 'text-emerald-700'
        : 'text-muted';

  return (
    <span
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-1.5 text-[12px] font-medium ${tone}`}
    >
      {(status === 'saving' || status === 'pending') && (
        <span aria-hidden="true" className="h-2 w-2 animate-pulse rounded-full bg-current" />
      )}
      {label}
    </span>
  );
}
